'use client';

import {
  AppearanceProps,
  ProductCard as BaseProductCard,
  ProductCardBackground,
  ProductCardMediaRef,
} from '@sprd/sprd-component-kit/v2';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import NextLink from 'next/link';
import { useIntersectionObserver } from 'usehooks-ts';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { NonIndexableLink } from '@/components/NonIndexableLink/NonIndexableLink';
import {
  SprdResponsiveImage,
  SprdResponsiveImageData,
} from '@/components/ResponsiveImage/SprdResponsiveImage';
import { GetPathNameArgs, getPathname } from '@/i18n/navigation';
import { AppearanceImage } from '@/utility/appearanceConfig';
import { Assortment } from '@/utility/shopIds';
import { FavoriteButton, FavoriteButtonItem } from '../FavoriteButton/FavoriteButton';
import { DiscountedPrice } from '../Price/DiscountedPrice';
import { PriceProps } from '../Price/Price';
import { useResolvedAppearances } from './AppearanceConfigProvider';

export type ProductCardProps = {
  className?: string;
  background?: ProductCardBackground;
  title: string;
  subTitle?: string;
  /** Extra line rendered below the title (e.g. designer/license attribution). */
  provider?: React.ReactNode;
  imageBaseUrl: string;
  images: SprdResponsiveImageData[];
  imageSizes?: string;
  /** Rendered image dimensions requested from the image service. Defaults to `SprdResponsiveImage`'s 450x600. */
  imageWidth?: number;
  imageHeight?: number;
  defaultAppearanceId?: string;
  appearances?: AppearanceImage[];
  /**
   * Alternative to `appearances`: resolve the full appearance data from the
   * nearest AppearanceConfigProvider. Keeps the RSC payload small on list pages
   * by serializing the deduplicated appearance map once instead of per card.
   */
  appearanceIds?: string[];
  price: number;
  currentDiscountAmount?: number;
  link: {
    indexable?: boolean;
    href: GetPathNameArgs['href'];
    onClick?: (e: React.MouseEvent<HTMLElement>, appearanceId: string) => void;
  };
  favorite?: FavoriteButtonItem;
  /**
   * Marks the card as out of stock: mutes media/text, hides badges and renders
   * `banner` along the bottom of the media area.
   */
  unavailable?: { isUnavailable: boolean; banner: string };
  /** Content rendered below the card body (e.g. add-to-basket actions). */
  bottomSlot?: React.ReactNode;
  playMobileSwipeHint?: boolean;
  labels?: {
    label: string;
    position: 'bottom-left' | 'top-left';
  }[];
  productTypeId: string;
  // which shop the product belongs to, for its strike price (default: marketplace)
  assortment?: Assortment;
  priceFormat?: PriceProps['format'];
  brand?: string;
  dataTracking?: string;
  optimizeImages?: boolean;
  /**
   * When true, the appearance swatches are not server-rendered; they render
   * client-side once the card scrolls near the viewport. Used on list pages
   * to keep the SSR HTML small for below-the-fold cards.
   */
  deferAppearances?: boolean;
  /** Same as `deferAppearances`, but for the badges. */
  deferBadges?: boolean;
};

type ImageOrPaceholder = SprdResponsiveImageData | { placeholder: true };

function isPlaceholder(image: ImageOrPaceholder): image is { placeholder: true } {
  return (image as { placeholder: true }).placeholder === true;
}

const ProductCard: React.FC<ProductCardProps> = ({
  className,
  background,
  title,
  subTitle,
  provider,
  imageBaseUrl,
  images,
  imageWidth,
  imageHeight,
  link,
  price,
  priceFormat,
  currentDiscountAmount,
  defaultAppearanceId,
  appearances,
  appearanceIds,
  favorite,
  unavailable,
  bottomSlot,
  playMobileSwipeHint = false,
  labels,
  productTypeId,
  assortment = 'marketplace',
  brand,
  dataTracking,
  optimizeImages = false,
  deferAppearances = false,
  deferBadges = false,
}) => {
  const t = useTranslations('productCard');
  const mediaRef = useRef<ProductCardMediaRef>(null);
  const locale = useLocale();
  const isDesktop = useIsDesktop();
  const resolvedAppearances = useResolvedAppearances(appearanceIds);
  const effectiveAppearances =
    appearanceIds !== undefined ? resolvedAppearances : (appearances ?? []);
  const [hoveredAppearance, setHoveredAppearance] = useState<AppearanceProps>();

  const indexable = link.indexable ?? true;
  const Link = useMemo(() => (indexable ? NextLink : NonIndexableLink), [indexable]);

  const { ref: observeCard, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
    rootMargin: '200px',
  });

  const [hasSliderInteraction, setHasSliderInteraction] = useState(false);

  const handleImageProgressChange = useCallback((progress: number) => {
    if (progress > 0) setHasSliderInteraction(true);
  }, []);

  // With optimizeImages, the server renders only the first image plus a single
  // placeholder slide to keep the SSR DOM minimal. Once the card scrolls near
  // the viewport, the slider expands to the full slide count with the second
  // image loaded, so it is fetched before the user can swipe to it. The
  // remaining images swap into their placeholder slides on the first slider
  // interaction. Changing the slide count makes Embla reInit and cut off a
  // running snap animation, so it may only happen on intersection (while the
  // slider is guaranteed to be at rest) — never on interaction.
  const displayImages: ImageOrPaceholder[] = (() => {
    if (!optimizeImages || hasSliderInteraction) return images;
    if (!isIntersecting) return [images[0]!, { placeholder: true }];
    return images.map((image, index) => (index < 2 ? image : { placeholder: true }));
  })();

  // Play the mobile swipe hint once the card scrolls into view.
  useEffect(() => {
    if (!isIntersecting || !(playMobileSwipeHint && !isDesktop)) return;
    if (window.productCardAnimationPlayed === true || !mediaRef.current) return;

    window.productCardAnimationPlayed = true;
    void mediaRef.current.playSwipeAnimation();
  }, [isIntersecting, playMobileSwipeHint, isDesktop]);

  const getLinkHref = useCallback(
    (appearanceId: string | undefined): string =>
      getPathname({
        href:
          typeof link.href === 'string'
            ? {
                pathname: link.href,
                query: appearanceId ? { appearance: appearanceId } : undefined,
              }
            : {
                ...link.href,
                query:
                  link.href.query || appearanceId
                    ? {
                        ...link.href.query,
                        ...(appearanceId ? { appearance: appearanceId } : undefined),
                      }
                    : undefined,
              },
        locale,
      }),
    [link.href, locale]
  );

  const appearanceItems = effectiveAppearances.map((appearance) => ({
    id: appearance.id,
    name: appearance.name,
    colors: appearance.colors,
    textureImage: appearance.textureImage,
  }));

  return (
    <BaseProductCard.Root
      ref={observeCard}
      className={className}
      background={background}
      unavailable={unavailable?.isUnavailable}
    >
      <BaseProductCard.Link asChild>
        <Link
          href={getLinkHref(hoveredAppearance?.id ?? defaultAppearanceId)}
          aria-label={t('a11y.goToProduct', { product: title })}
          onClick={(event) =>
            link.onClick?.(
              event,
              hoveredAppearance?.id ?? defaultAppearanceId ?? effectiveAppearances[0]!.id
            )
          }
          prefetch={false}
          data-tracking={dataTracking}
        >
          <BaseProductCard.Media
            ref={mediaRef}
            className="aspect-[3/4]"
            navigation={
              !isIntersecting
                ? { visible: false }
                : {
                    visible: true,
                    nextLabel: t('a11y.nextImage'),
                    prevLabel: t('a11y.previousImage'),
                  }
            }
            activeImage={
              hoveredAppearance ? (
                <SprdResponsiveImage
                  imageBaseUrl={imageBaseUrl}
                  image={{
                    ...images[0]!,
                    appearanceId: hoveredAppearance.id,
                  }}
                  defaultImageWidth={imageWidth}
                  defaultImageHeight={imageHeight}
                />
              ) : undefined
            }
            onImageProgressChange={optimizeImages ? handleImageProgressChange : undefined}
          >
            {displayImages.map((image, index) =>
              isPlaceholder(image) ? (
                <div key={index} className="aspect-[3/4] bg-gray-200" />
              ) : (
                <SprdResponsiveImage
                  key={index}
                  imageBaseUrl={imageBaseUrl}
                  image={image}
                  defaultImageWidth={imageWidth}
                  defaultImageHeight={imageHeight}
                />
              )
            )}
            {(!deferBadges || isIntersecting) && !!labels && (
              <BaseProductCard.Badges items={labels} className="m-2" />
            )}
            {isIntersecting && (
              <BaseProductCard.HoverAppearances
                items={appearanceItems}
                onHover={setHoveredAppearance}
              />
            )}
            {unavailable?.isUnavailable && (
              <BaseProductCard.Banner>{unavailable.banner}</BaseProductCard.Banner>
            )}
          </BaseProductCard.Media>
          <BaseProductCard.Info
            price={
              <DiscountedPrice
                price={Number(price)}
                productTypeId={productTypeId}
                format={priceFormat}
                currentDiscountAmount={currentDiscountAmount ? currentDiscountAmount : 0}
                assortment={assortment}
              />
            }
            title={
              <>
                {!!brand && <span className="font-bold">{brand}</span>}
                <span className="flex flex-col gap-1.5">
                  <span className={clsx('truncate', brand && 'font-normal!')}>{title}</span>
                  {!!subTitle && <span className="truncate">{subTitle}</span>}
                </span>
              </>
            }
          >
            {!!provider && <p className="text-xs">{provider}</p>}
            {(!deferAppearances || isIntersecting) && (
              <BaseProductCard.Appearances items={appearanceItems} />
            )}
          </BaseProductCard.Info>
        </Link>
      </BaseProductCard.Link>
      {favorite && (
        <BaseProductCard.Floating>
          <FavoriteButton {...favorite} />
        </BaseProductCard.Floating>
      )}
      {!!bottomSlot && <BaseProductCard.Footer>{bottomSlot}</BaseProductCard.Footer>}
    </BaseProductCard.Root>
  );
};

export default ProductCard;
